/**
 * The drifting particle field behind the sign-in.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS ~90 LINES OF CANVAS AND NOT `particles.js`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The effect asked for is the well-known particles.js look: points drifting, with a line
 * drawn between any two that come close. That library is ~25 KB gzipped, unmaintained since
 * 2016, mounts by scanning the DOM for an element id, and reads its configuration from a
 * JSON file fetched at runtime. Every one of those is a liability in a bundled React app,
 * and the sign-in is the one screen that must load when everything else is uncertain.
 *
 * The algorithm is a nested loop over a few dozen points. Writing it here costs less than
 * integrating the library, and buys the things that actually matter on this screen: it stops
 * when the tab is hidden, it stops entirely under `prefers-reduced-motion`, it scales the
 * count to the viewport, and it never touches the DOM outside its own canvas.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT KEEPS IT CHEAP
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * DENSITY IS PER AREA, NOT A FIXED COUNT. A count tuned on a laptop is a haze on a 4K
 * monitor and a swarm on a phone. Points are derived from the canvas area and capped, so a
 * large display gets a sparser field rather than a slower one.
 *
 * THE LINK PASS IS THE EXPENSIVE HALF — it is O(n²), and n is capped at 90 for exactly that
 * reason: 90 points is 4,005 distance checks a frame, which is nothing, while 300 would be
 * 44,850 and would show up on a mid-range laptop.
 *
 * DPR IS APPLIED TO THE BACKING STORE, not the CSS size, or the lines render soft on a
 * Retina display — the one artefact that makes an effect like this look cheap.
 */
import { useEffect, useRef, type ReactNode } from 'react';

/** Points per million CSS pixels. Tuned so 1512x900 yields about 60. */
const DENSITY = 44;
const MAX_POINTS = 90;
/** Two points link when closer than this, in CSS pixels. */
const LINK_DISTANCE = 132;

interface Point {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
}

export function ParticleField(): ReactNode {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    /*
     * Motion is decoration here. Someone who has asked the system for less of it gets a
     * still canvas — not a slower one, none — and the page is identical in every other way.
     */
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let points: Point[] = [];
    let raf = 0;
    let w = 0;
    let h = 0;

    /*
     * THE FIELD'S TWO COLOURS ARE THE PRODUCT'S, not this file's: `--si-line-rgb` and
     * `--si-dot-rgb`, set on the sign-in frame in signin.css (Thunder's cyan; Cratus's red). Read
     * as `r, g, b` so the per-frame alpha can be appended. The fallbacks are the literals these
     * replaced, so a frame that sets neither draws exactly what it always did.
     */
    const style = getComputedStyle(canvas);
    const lineRgb = style.getPropertyValue('--si-line-rgb').trim() || '125, 211, 252';
    const dotRgb = style.getPropertyValue('--si-dot-rgb').trim() || '186, 230, 253';

    const seed = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      /* Draw in CSS pixels; the backing store carries the density. */
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const n = Math.min(MAX_POINTS, Math.round(((w * h) / 1_000_000) * DENSITY));
      points = Array.from({ length: n }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        /* Slow. A field that races reads as a screensaver, not as a surface. */
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: 0.9 + Math.random() * 1.5,
      }));
    };

    const frame = (): void => {
      ctx.clearRect(0, 0, w, h);

      for (const p of points) {
        p.x += p.vx;
        p.y += p.vy;
        /* Wrap rather than bounce: a bounce puts a visible seam along each edge where
           points pile up and reverse together. */
        if (p.x < -20) p.x = w + 20;
        if (p.x > w + 20) p.x = -20;
        if (p.y < -20) p.y = h + 20;
        if (p.y > h + 20) p.y = -20;
      }

      /* Links first, so the points sit on top of their own lines. */
      for (let i = 0; i < points.length; i += 1) {
        const a = points[i]!;
        for (let j = i + 1; j < points.length; j += 1) {
          const b = points[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > LINK_DISTANCE * LINK_DISTANCE) continue;
          /* Compared squared above; the root is only paid for the pairs that link. */
          const alpha = (1 - Math.sqrt(d2) / LINK_DISTANCE) * 0.22;
          ctx.strokeStyle = `rgba(${lineRgb}, ${alpha})`;
          ctx.lineWidth = 0.7;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      for (const p of points) {
        ctx.fillStyle = `rgba(${dotRgb}, 0.55)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = window.requestAnimationFrame(frame);
    };

    const start = (): void => {
      if (raf !== 0) return;
      raf = window.requestAnimationFrame(frame);
    };
    const stop = (): void => {
      if (raf === 0) return;
      window.cancelAnimationFrame(raf);
      raf = 0;
    };

    seed();
    if (reduced) {
      /* One frame, then nothing. The field is composed and completely still. */
      ctx.clearRect(0, 0, w, h);
      for (const p of points) {
        ctx.fillStyle = `rgba(${dotRgb}, 0.4)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    } else {
      start();
    }

    /*
     * A background animation must not run against a tab nobody is looking at. Without this
     * the loop keeps a core busy behind whatever the operator switched to.
     */
    const onVisibility = (): void => {
      if (reduced) return;
      if (document.hidden) stop();
      else start();
    };

    const ro = new ResizeObserver(() => {
      seed();
    });
    ro.observe(canvas);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <canvas ref={ref} className="lm-si-particles" aria-hidden="true" />;
}
